import type { components, paths } from '@feedfm/clips-services-api';
import { ClipsApiEventTarget } from './ClipsApiEventTarget';

export type AuthUpdateEventDetail =
  | components['schemas']['AuthenticatedUserResponse']
  | null;

export interface VersionUpdateEventDetail {
  servicesApiVersion: string | null;
  environment: 'local' | 'stage' | 'prod' | 'unknown';
}

export type BackendClientEvents = 'auth-update' | 'version-update';

export interface BackendClientEventMap {
  'auth-update': AuthUpdateEventDetail;
  'version-update': VersionUpdateEventDetail;
}

export class AuthUpdateEvent extends CustomEvent<AuthUpdateEventDetail> {
  constructor(detail: AuthUpdateEventDetail) {
    super('auth-update', { detail });
  }
}

export class VersionUpdateEvent extends CustomEvent<VersionUpdateEventDetail> {
  constructor(detail: VersionUpdateEventDetail) {
    super('version-update', { detail });
  }
}

/** Uses backend URL to infer backend environment (local, stage, or prod). */
function inferEnvironment(
  urlString: string,
): 'local' | 'stage' | 'prod' | 'unknown' {
  const url = new URL(urlString, window.location.origin);

  if (url.hostname.includes('localhost') || url.hostname.includes('127.0.0.1'))
    return 'local';

  if (url.hostname === 'services.clips.stage.feed.fm') return 'stage';
  if (url.hostname === 'services.clips.feed.fm') return 'prod';

  return 'unknown';
}

/**
 * Minimalist BackendClient class
 *
 * TODO: This should be deprecated in lieu of the generated client from
 * `@feedfm/clips-services-api-client`.
 */
export class BackendClient {
  /** The promise created by performRefresh(), used to prevent concurrent attempts. */
  private refreshPromise: Promise<boolean> | null = null;

  /**
   * The current version of the backend API. This is null (unknown) until the first response is
   * processed.
   */
  private servicesApiVersion: string | null = null;

  /** Only used in local development. */
  private proxyTarget: string | null;

  /** The EventTarget API for clips API events. */
  events: ClipsApiEventTarget = new ClipsApiEventTarget();

  constructor(
    private baseUrl: string = '/',
    private sessionToken: string | null = null,
  ) {
    this.proxyTarget = process.env.REACT_APP__PROXY_TARGET || null;
  }

  /** Update auth state in the client. */
  setAuthToken(token: string | null) {
    this.sessionToken = token;
  }

  /**
   * Returns true if the client is authenticated (has a session token).
   */
  isAuthenticated() {
    return this.sessionToken !== null;
  }

  async login(
    email: string,
    password: string,
    debugSettings?: {
      sessionDurationSec?: number;
      refreshDurationSec?: number;
    },
  ): Promise<components['schemas']['LoginResponse']> {
    const headers: Record<string, string> = {};

    // Add debug headers if provided
    if (debugSettings?.sessionDurationSec) {
      headers['x-fmg-session-timeout'] =
        debugSettings.sessionDurationSec.toString();
    }
    if (debugSettings?.refreshDurationSec) {
      headers['x-fmg-refresh-token-timeout'] =
        debugSettings.refreshDurationSec.toString();
    }

    const result = await this.fetch<components['schemas']['LoginResponse']>(
      '/login',
      {
        method: 'POST',
        body: JSON.stringify({ email, password }),
        credentials: 'include',
        headers,
      },
    );

    this.events.dispatchAuthUpdateEvent(result);

    return result;
  }

  private async performRefresh(): Promise<boolean> {
    if (this.refreshPromise) {
      // If already refreshing, wait for the existing refresh to complete
      return this.refreshPromise;
    }

    this.refreshPromise = this._doRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async _doRefresh(): Promise<boolean> {
    try {
      const response = await this._makeRequest('/refresh', {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(
          `Refresh failed: ${response.status} ${response.statusText}`,
        );
      }

      const refreshData: components['schemas']['RefreshTokenResponse'] =
        await response.json();

      // Update the session token
      this.sessionToken = refreshData.token;

      // Notify the context about the auth update
      this.events.dispatchAuthUpdateEvent(refreshData);

      return true;
    } catch (error) {
      console.error('Failed to refresh token:', error);

      // Clear the session token on refresh failure
      this.sessionToken = null;
      this.events.dispatchAuthUpdateEvent(null);

      return false;
    }
  }

  async logout(): Promise<components['schemas']['LogoutResponse']> {
    return this.fetch<components['schemas']['LogoutResponse']>('/logout', {
      method: 'POST',
    });
  }

  async collections(
    collectionIds?: number[],
  ): Promise<components['schemas']['CollectionsResponse']> {
    const searchParams = new URLSearchParams();
    if (collectionIds?.length) {
      searchParams.set('collectionIds', collectionIds.join(','));
    }
    const params = searchParams.toString() ? `?${searchParams.toString()}` : '';

    return this.fetch<components['schemas']['CollectionsResponse']>(
      `/collections${params}`,
      {
        method: 'GET',
      },
    );
  }

  async collectionById(
    collectionId: number,
  ): Promise<components['schemas']['CollectionDetailResponse']> {
    return this.fetch<components['schemas']['CollectionDetailResponse']>(
      `/collections/${collectionId}`,
      {
        method: 'GET',
      },
    );
  }

  async validateToken(
    temporaryToken: string,
  ): Promise<components['schemas']['ValidateTokenResponse']> {
    return this.fetch<components['schemas']['ValidateTokenResponse']>(
      '/validate-token',
      {
        method: 'POST',
        body: JSON.stringify({ temporaryToken }),
      },
    );
  }

  async resetPassword(
    temporaryToken: string,
    newPassword: string,
  ): Promise<components['schemas']['ResetPasswordResponse']> {
    return this.fetch<components['schemas']['ResetPasswordResponse']>(
      '/reset-password',
      {
        method: 'POST',
        body: JSON.stringify({ temporaryToken, newPassword }),
      },
    );
  }

  async getApplicationCredentials(
    appId: number,
  ): Promise<components['schemas']['ApplicationCredentialsResponse']> {
    return this.fetch<components['schemas']['ApplicationCredentialsResponse']>(
      `/application/${appId}`,
      {
        method: 'GET',
      },
    );
  }

  async createAcrCloudCredentials(
    appId: number,
    accessKey: string,
    secretKey: string,
    description?: string,
  ): Promise<components['schemas']['CreateAcrCloudCredentialsResponse']> {
    return this.fetch('/acr-cloud-credentials', {
      method: 'POST',
      body: JSON.stringify({
        appId,
        accessKey,
        secretKey,
        description,
      }),
    });
  }

  async getAcrCloudCredentials(
    appId: number,
  ): Promise<components['schemas']['AcrCloudCredentialsResponse']> {
    return this.fetch(`/application/${appId}/acr-cloud-credentials`, {
      method: 'GET',
    });
  }

  async search(params: {
    q: string;
    type?: 'collections' | 'clips' | 'all';
    collectionsLimit?: number;
    clipsLimit?: number;
  }): Promise<components['schemas']['SearchResponse']> {
    const searchParams = new URLSearchParams();
    searchParams.set('q', params.q);
    if (params.type) searchParams.set('type', params.type);
    if (params.collectionsLimit)
      searchParams.set('collectionsLimit', params.collectionsLimit.toString());
    if (params.clipsLimit)
      searchParams.set('clipsLimit', params.clipsLimit.toString());

    return this.fetch(`/search?${searchParams.toString()}`, {
      method: 'GET',
    });
  }

  async suggestClips(params: {
    q: string;
    limit?: number;
  }): Promise<
    paths['/search/suggestions']['get']['responses']['200']['content']['application/json']
  > {
    const searchParams = new URLSearchParams();
    searchParams.set('q', params.q);
    if (params.limit) searchParams.set('limit', params.limit.toString());

    return this.fetch(`/search/suggestions?${searchParams.toString()}`, {
      method: 'GET',
    });
  }

  async getCurationCatalog(
    params: components['schemas']['CurationCatalogRequest'],
  ): Promise<components['schemas']['CurationCatalogResponse']> {
    return this.fetch<components['schemas']['CurationCatalogResponse']>(
      '/curation/catalog',
      {
        method: 'POST',
        body: JSON.stringify(params),
      },
    );
  }

  async getCollectionEngagements(params: {
    appId: number;
    timePeriod: 'current-month' | 'last-3-months' | 'last-year';
  }): Promise<components['schemas']['CollectionEngagementsResponse']> {
    const searchParams = new URLSearchParams();
    searchParams.set('appId', params.appId.toString());
    searchParams.set('timePeriod', params.timePeriod);

    return this.fetch(`/analytics/collections?${searchParams.toString()}`, {
      method: 'GET',
    });
  }

  async getTrackEngagements(params: {
    appId: number;
    timePeriod: 'current-month' | 'last-3-months' | 'last-year';
  }): Promise<components['schemas']['TrackEngagementsResponse']> {
    const searchParams = new URLSearchParams();
    searchParams.set('appId', params.appId.toString());
    searchParams.set('timePeriod', params.timePeriod);

    return this.fetch(`/analytics/tracks?${searchParams.toString()}`, {
      method: 'GET',
    });
  }

  async getArtistEngagements(params: {
    appId: number;
    timePeriod: 'current-month' | 'last-3-months' | 'last-year';
  }): Promise<components['schemas']['ArtistEngagementsResponse']> {
    const searchParams = new URLSearchParams();
    searchParams.set('appId', params.appId.toString());
    searchParams.set('timePeriod', params.timePeriod);

    return this.fetch(`/analytics/artists?${searchParams.toString()}`, {
      method: 'GET',
    });
  }

  async getTotalPlaysStats(params: {
    appId: number;
  }): Promise<components['schemas']['TotalPlaysStatsResponse']> {
    const searchParams = new URLSearchParams();
    searchParams.set('appId', params.appId.toString());

    return this.fetch(`/analytics/total-plays?${searchParams.toString()}`, {
      method: 'GET',
    });
  }

  async getActiveUsersStats(params: {
    appId: number;
  }): Promise<components['schemas']['ActiveUsersStatsResponse']> {
    const searchParams = new URLSearchParams();
    searchParams.set('appId', params.appId.toString());

    return this.fetch(`/analytics/active-users?${searchParams.toString()}`, {
      method: 'GET',
    });
  }

  async getPopularItems(params: {
    appId: number;
  }): Promise<components['schemas']['PopularItemsResponse']> {
    const searchParams = new URLSearchParams();
    searchParams.set('appId', params.appId.toString());

    return this.fetch(`/analytics/popular-items?${searchParams.toString()}`, {
      method: 'GET',
    });
  }

  async similarTracks(
    params: NonNullable<paths['/similar-tracks']['get']['parameters']['query']>,
  ): Promise<
    paths['/similar-tracks']['get']['responses']['200']['content']['application/json']
  > {
    const searchParams = new URLSearchParams();
    if (params.audioUrl) searchParams.set('audioUrl', params.audioUrl);
    if (params.title) searchParams.set('title', params.title);
    if (params.displayArtistName)
      searchParams.set('displayArtistName', params.displayArtistName);
    if (params.limit) {
      searchParams.set('limit', params.limit.toString());
    }
    if (params.includeVibeTags) {
      searchParams.set('includeVibeTags', params.includeVibeTags.toString());
    }

    return this.fetch(`/similar-tracks?${searchParams.toString()}`, {
      method: 'GET',
    });
  }

  async submitSimilarTrackFeedback(
    params: components['schemas']['SimilarTrackFeedbackRequest'] & {
      isTest?: boolean;
    },
  ): Promise<components['schemas']['SimilarTrackFeedbackResponse']> {
    const searchParams = new URLSearchParams();
    if (params.isTest) {
      searchParams.set('isTest', 'true');
    }

    return this.fetch(`/similar-tracks/feedback?${searchParams.toString()}`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async getFeedbackForTracks(params: {
    trackEmbeddingIds: number[];
    includeTest?: boolean;
    seedSongId?: number;
  }): Promise<components['schemas']['GetTrackFeedbackResponse']> {
    const searchParams = new URLSearchParams();
    searchParams.set('trackEmbeddingIds', params.trackEmbeddingIds.join(','));
    if (params.includeTest) {
      searchParams.set('includeTest', 'true');
    }
    if (params.seedSongId !== undefined) {
      searchParams.set('seedSongId', params.seedSongId.toString());
    }

    return this.fetch(`/similar-tracks/feedback?${searchParams.toString()}`, {
      method: 'GET',
    });
  }

  async tracksByVibeTag(
    params: NonNullable<
      paths['/similar-tracks/by-vibe']['get']['parameters']['query']
    >,
  ): Promise<
    paths['/similar-tracks/by-vibe']['get']['responses']['200']['content']['application/json']
  > {
    const searchParams = new URLSearchParams();
    searchParams.set('vibeTagId', params.vibeTagId.toString());
    if (params.limit) {
      searchParams.set('limit', params.limit.toString());
    }

    return this.fetch(`/similar-tracks/by-vibe?${searchParams.toString()}`, {
      method: 'GET',
    });
  }

  async fallbackSearch(
    params: NonNullable<
      paths['/similar-tracks/fallback-search']['get']['parameters']['query']
    >,
  ): Promise<
    paths['/similar-tracks/fallback-search']['get']['responses']['200']['content']['application/json']
  > {
    const searchParams = new URLSearchParams();
    searchParams.set('q', params.q);

    return this.fetch(
      `/similar-tracks/fallback-search?${searchParams.toString()}`,
      { method: 'GET' },
    );
  }

  async similarClipsByAudioFile(
    params: NonNullable<
      paths['/similar-tracks/by-audio-file']['get']['parameters']['query']
    >,
  ): Promise<
    paths['/similar-tracks/by-audio-file']['get']['responses']['200']['content']['application/json']
  > {
    const searchParams = new URLSearchParams();
    searchParams.set('audioFileId', params.audioFileId.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());

    return this.fetch(
      `/similar-tracks/by-audio-file?${searchParams.toString()}`,
      { method: 'GET' },
    );
  }

  async similarClipsByArtist(
    params: NonNullable<
      paths['/similar-tracks/by-artist']['get']['parameters']['query']
    >,
  ): Promise<
    paths['/similar-tracks/by-artist']['get']['responses']['200']['content']['application/json']
  > {
    const searchParams = new URLSearchParams();
    searchParams.set('artistName', params.artistName);
    if (params.limit) searchParams.set('limit', params.limit.toString());

    return this.fetch(
      `/similar-tracks/by-artist?${searchParams.toString()}`,
      { method: 'GET' },
    );
  }

  async semanticSearch(
    params: { query: string; limit?: number },
  ): Promise<
    paths['/similar-tracks/semantic-search']['post']['responses']['200']['content']['application/json']
  > {
    return this.fetch('/similar-tracks/semantic-search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
  }

  async getThumbnail(
    params: paths['/artwork/thumbnail']['get']['parameters']['query'],
  ): Promise<
    paths['/artwork/thumbnail']['get']['responses']['200']['content']['application/json']
  > {
    const searchParams = new URLSearchParams();
    searchParams.set('artworkIds', params.artworkIds.join(','));
    if (params.width !== undefined) {
      searchParams.set('width', params.width.toString());
    }
    if (params.height !== undefined) {
      searchParams.set('height', params.height.toString());
    }
    if (params.format !== undefined) {
      searchParams.set('format', params.format);
    }
    if (params.preserveOrder !== undefined) {
      searchParams.set('preserveOrder', params.preserveOrder ? 'true' : 'false');
    }

    return this.fetch(`/artwork/thumbnail?${searchParams.toString()}`, {
      method: 'GET',
    });
  }

  /**
   * Fetch a resource from the backend. Will automatically retry once on 401 errors after attempting
   * to refresh the session token.
   */
  async fetch<T = unknown>(
    path: string,
    options: Omit<RequestInit, 'headers'> & {
      headers?: Record<string, string>;
    } = {},
  ): Promise<T> {
    // First attempt
    let response = await this._makeRequest(path, options);

    // If 401 and we can refresh, try refresh + retry
    if (response.status === 401 && this.sessionToken) {
      const refreshSuccess = await this.performRefresh();
      if (refreshSuccess) {
        // Retry with new token
        response = await this._makeRequest(path, options);
      } else {
        // Refresh failed - context already notified by performRefresh
        throw new Error('Authentication failed');
      }
    }

    const servicesVersionHeader = response.headers.get(
      'x-fmg-clips-services-api-version',
    );

    // We only trigger an update when the version updates to a new non-null value.
    if (
      servicesVersionHeader &&
      servicesVersionHeader !== this.servicesApiVersion
    ) {
      this.servicesApiVersion = servicesVersionHeader;
      this.events.dispatchVersionUpdateEvent({
        servicesApiVersion: this.servicesApiVersion,
        environment: inferEnvironment(this.proxyTarget || this.baseUrl),
      });
    }

    // Handle final response
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `API error: ${response.status} ${response.statusText} - ${errorText}`,
      );
    }

    return response.json();
  }

  /**
   * Makes a raw HTTP request without any retry logic.
   */
  private async _makeRequest(
    path: string,
    options: Omit<RequestInit, 'headers'> & {
      headers?: Record<string, string>;
    } = {},
  ): Promise<Response> {
    const normalizedPath = path.startsWith('/') ? path : '/' + path;
    const url = `${this.baseUrl}${normalizedPath}`;
    const headers = this._makeRequestHeaders(path, options);

    const response = window.fetch(url, {
      ...options,
      headers,
    });

    return response;
  }

  private _makeRequestHeaders(
    _path: string,
    options: Omit<RequestInit, 'headers'> & {
      headers?: Record<string, string>;
    },
  ): Record<string, string> {
    const incomingHeaders = options.headers || {};
    const extraHeaders: Record<string, string> = {};

    // Only set Content-Type if we are sending a body (mostly POST/PUT/PATCH)
    if (options.body != null && options.method && options.method !== 'GET') {
      extraHeaders['Content-Type'] = 'application/json';
    }

    if (this.sessionToken) {
      extraHeaders['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    return {
      ...incomingHeaders,
      ...extraHeaders,
    };
  }
}
